
import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Twitter, Facebook, Linkedin, Instagram, Youtube, Globe, Link, MessageSquare } from 'lucide-react';

interface SocialConnectionProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const SocialConnectionProps: React.FC<SocialConnectionProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [socialConnections, setSocialConnections] = useState<Record<string, string>>({
    twitter: '',
    facebook: '',
    linkedin: '',
    instagram: '',
    youtube: '',
    website: '',
    ...formData.socialConnections
  });
  
  const handleInputChange = (platform: string, value: string) => {
    const updated = {
      ...socialConnections,
      [platform]: value
    };
    setSocialConnections(updated);
    updateFormData({ socialConnections: updated });
  };
  
  const socialPlatforms = [
    { 
      id: 'twitter', 
      name: 'Twitter', 
      icon: Twitter, 
      placeholder: 'username',
      prefix: 'twitter.com/'
    },
    { 
      id: 'facebook', 
      name: 'Facebook', 
      icon: Facebook,
      placeholder: 'username or page name',
      prefix: 'facebook.com/'
    },
    { 
      id: 'linkedin', 
      name: 'LinkedIn', 
      icon: Linkedin,
      placeholder: 'profile url or username',
      prefix: 'linkedin.com/in/'
    },
    { 
      id: 'instagram', 
      name: 'Instagram', 
      icon: Instagram,
      placeholder: 'username',
      prefix: 'instagram.com/'
    },
    { 
      id: 'youtube', 
      name: 'YouTube', 
      icon: Youtube,
      placeholder: 'channel name or ID',
      prefix: 'youtube.com/'
    },
    { 
      id: 'website', 
      name: 'Website', 
      icon: Globe,
      placeholder: 'yourwebsite.com',
      prefix: 'https://'
    },
  ];

  return (
    <OnboardingLayout
      title="Connect Your Social Profiles"
      subtitle="Link your existing social media accounts to build your network"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
      nextLabel="Complete Setup"
    >
      <div className="max-w-3xl mx-auto">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Your Social Profiles</h3>
            <p className="text-muted-foreground mb-6">
              Connect your existing social profiles to help readers find you and to share your content.
              All fields are optional.
            </p>
            
            <div className="space-y-4">
              {socialPlatforms.map((platform) => (
                <div key={platform.id} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-muted text-muted-foreground">
                    <platform.icon className="h-4 w-4" />
                  </div>
                  
                  <div>
                    <Label htmlFor={platform.id} className="text-sm font-medium">
                      {platform.name}
                    </Label>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap mr-1">
                        {platform.prefix}
                      </div>
                      <Input
                        id={platform.id}
                        value={socialConnections[platform.id] || ''}
                        onChange={(e) => handleInputChange(platform.id, e.target.value)}
                        placeholder={platform.placeholder}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start">
              <MessageSquare className="h-10 w-10 text-primary mr-4 mt-1" />
              <div>
                <h3 className="text-lg font-medium mb-2">Announce your DirectPress presence</h3>
                <p className="text-muted-foreground mb-4">
                  After completing setup, you'll have the option to announce your DirectPress profile
                  on your connected social accounts to help your existing audience find you here.
                </p>
                <Button variant="outline" onClick={onNext}>
                  I'll do this later
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

export default SocialConnectionProps;
